import { HELD_ACTIONS, type ActionId } from "./actions.ts";
import { codeShaped, drawn } from "./key-shape.ts";
import { codeLabel } from "./layout.ts";
import { reservedCode, type ReservedReason } from "./reserved.ts";

/**
 * What a press does while a key cap waits for one (ADR-061's amendments "key rebinding", decision 6, and "every key
 * rebindable but Escape", point 6): the physical key it is on is taken, whatever it types; Escape cancels; a modifier
 * pressed alone is taken at once; a press with Ctrl, Alt or Meta held with another key is the browser's shortcut; a key
 * the browser keeps or cannot name is refused with why; and a held action is refused Tab.
 */

/**
 * What a press means to a waiting cap: cancel the wait, a key refused with why, or the physical key taken.
 */
export type Capture = { readonly kind: "cancel" } | { readonly kind: "refused"; readonly code: string; readonly reason: RefusedReason } | { readonly kind: "key"; readonly code: string };

/**
 * Why a press is refused: a reserved key's reason, a shortcut, a key whose name the pixel face cannot draw, or Tab for
 * a held action.
 */
export type RefusedReason = ReservedReason | "shortcut" | "undrawn" | "focus";

/**
 * The physical keys a modifier is on: pressed alone, each is taken as a key.
 */
const MODIFIERS: ReadonlySet<string> = new Set(["ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight", "AltLeft", "AltRight", "MetaLeft", "MetaRight", "OSLeft", "OSRight"]);

/**
 * What a press does while a cap waits.
 *
 * @param event The press: its key, its physical key when the event says, and the modifiers held.
 * @param action The action the cap is for; a held action is refused Tab.
 * @returns What the press means.
 * @example
 * captureKey({ key: "a", code: "KeyQ" });
 * // { kind: "key", code: "KeyQ" }: AZERTY's A key is where Q is
 */
export function captureKey(event: { readonly key: string; readonly code?: string; readonly shiftKey?: boolean; readonly ctrlKey?: boolean; readonly altKey?: boolean; readonly metaKey?: boolean }, action?: ActionId): Capture {
  const code = event.code ?? "";
  if (event.key === "Escape" || code === "Escape") {
    return { kind: "cancel" };
  }
  if (!codeShaped(code)) {
    return { kind: "refused", code, reason: "unnamed" };
  }
  if (MODIFIERS.has(code)) {
    return { kind: "key", code };
  }
  if (event.ctrlKey === true || event.altKey === true || event.metaKey === true) {
    return { kind: "refused", code, reason: "shortcut" };
  }
  const reason = reservedCode(code);
  if (reason !== null) {
    return { kind: "refused", code, reason };
  }
  if (code === "Tab" && action !== undefined && HELD_ACTIONS.includes(action)) {
    return { kind: "refused", code, reason: "focus" };
  }
  return drawn(codeLabel(code)) ? { kind: "key", code } : { kind: "refused", code, reason: "undrawn" };
}
